import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trophy, Flame, Zap, Medal, Crown } from "lucide-react";

const Leaderboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const data = await api.getLeaderboard(20);
      setLeaderboard(data);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-slate-400" />;
      case 3:
        return <Medal className="w-6 h-6 text-orange-400" />;
      default:
        return null;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return "bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-300";
      case 2:
        return "bg-gradient-to-r from-slate-50 to-slate-100 border-slate-300";
      case 3:
        return "bg-gradient-to-r from-orange-50 to-orange-100 border-orange-300";
      default:
        return "bg-white border-slate-200";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-bounce-subtle">
          <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-white">A</span>
          </div>
        </div>
      </div>
    );
  }

  // Find current user's rank
  const userRank = leaderboard.find(entry => entry.name === user?.name);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="leaderboard-page">
      {/* Header */}
      <div className="text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Trophy className="w-10 h-10 text-white" />
        </div>
        <h1 className="font-heading font-black text-3xl text-slate-800">Leaderboard</h1>
        <p className="text-slate-500 mt-2">Top learners this week</p>
      </div>

      {/* User's Rank Card */}
      {userRank && (
        <div className="card-pop bg-gradient-to-r from-lime-50 to-lime-100 border-lime-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-lime-500 rounded-full flex items-center justify-center text-white font-black text-xl shadow-[0_3px_0_0_#65a30d]">
                #{userRank.rank}
              </div>
              <div>
                <p className="font-heading font-bold text-lg text-slate-800">Your Rank</p>
                <p className="text-lime-600 font-medium">{userRank.xp} XP</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-orange-500">
                <Flame className="w-5 h-5" />
                <span className="font-bold">{userRank.streak} day streak</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top 3 Podium */}
      <div className="grid grid-cols-3 gap-4 mb-2">
        {/* 2nd Place */}
        <div className="order-1">
          {leaderboard[1] && (
            <div className="card-pop text-center pt-8 pb-4 border-slate-300 bg-gradient-to-b from-slate-50 to-slate-100">
              <div className="w-16 h-16 bg-slate-300 rounded-full flex items-center justify-center mx-auto mb-2 border-4 border-white shadow-lg">
                <span className="text-xl font-black text-slate-700">
                  {leaderboard[1].name.charAt(0).toUpperCase()}
                </span>
              </div>
              <Medal className="w-8 h-8 text-slate-400 mx-auto mb-1" />
              <p className="font-bold text-slate-700 truncate px-2">{leaderboard[1].name}</p>
              <p className="text-indigo-500 font-bold">{leaderboard[1].xp} XP</p>
            </div>
          )}
        </div>

        {/* 1st Place */}
        <div className="order-0 lg:order-1">
          {leaderboard[0] && (
            <div className="card-pop text-center pt-4 pb-4 border-yellow-300 bg-gradient-to-b from-yellow-50 to-yellow-100 -mt-4">
              <Crown className="w-10 h-10 text-yellow-500 mx-auto mb-2" />
              <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-2 border-4 border-white shadow-lg">
                <span className="text-2xl font-black text-yellow-900">
                  {leaderboard[0].name.charAt(0).toUpperCase()}
                </span>
              </div>
              <p className="font-bold text-slate-700 truncate px-2">{leaderboard[0].name}</p>
              <p className="text-indigo-500 font-bold text-lg">{leaderboard[0].xp} XP</p>
              {leaderboard[0].streak > 0 && (
                <div className="flex items-center justify-center gap-1 text-orange-500 mt-1">
                  <Flame className="w-4 h-4" />
                  <span className="text-sm font-bold">{leaderboard[0].streak}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 3rd Place */}
        <div className="order-2">
          {leaderboard[2] && (
            <div className="card-pop text-center pt-8 pb-4 border-orange-300 bg-gradient-to-b from-orange-50 to-orange-100">
              <div className="w-16 h-16 bg-orange-300 rounded-full flex items-center justify-center mx-auto mb-2 border-4 border-white shadow-lg">
                <span className="text-xl font-black text-orange-800">
                  {leaderboard[2].name.charAt(0).toUpperCase()}
                </span>
              </div>
              <Medal className="w-8 h-8 text-orange-400 mx-auto mb-1" />
              <p className="font-bold text-slate-700 truncate px-2">{leaderboard[2].name}</p>
              <p className="text-indigo-500 font-bold">{leaderboard[2].xp} XP</p>
            </div>
          )}
        </div>
      </div>

      {/* Rest of Leaderboard */}
      <div className="card-pop">
        <div className="space-y-2">
          {leaderboard.slice(3).map((entry) => {
            const isCurrentUser = entry.name === user?.name;
            
            return (
              <div
                key={entry.rank}
                className={`flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${
                  isCurrentUser 
                    ? "bg-lime-50 border-lime-200" 
                    : "border-transparent hover:bg-slate-50"
                }`}
              >
                {/* Rank */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  isCurrentUser 
                    ? "bg-lime-500 text-white" 
                    : "bg-slate-100 text-slate-600"
                }`}>
                  {entry.rank}
                </div>

                {/* Avatar & Name */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    isCurrentUser ? "bg-lime-500" : "bg-indigo-500"
                  }`}>
                    {entry.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-700 truncate">
                      {entry.name}
                      {isCurrentUser && <span className="text-lime-500 ml-2">(You)</span>}
                    </p>
                    <p className="text-sm text-slate-400">Level {entry.level}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4">
                  {entry.streak > 0 && (
                    <div className="flex items-center gap-1 text-orange-500">
                      <Flame className="w-4 h-4" />
                      <span className="font-bold text-sm">{entry.streak}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-indigo-500">
                    <Zap className="w-4 h-4" />
                    <span className="font-bold">{entry.xp}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Empty State */}
      {leaderboard.length === 0 && (
        <div className="card-pop text-center py-12">
          <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading font-bold text-xl text-slate-600">No rankings yet</h3>
          <p className="text-slate-400 mt-2">Start learning to appear on the leaderboard!</p>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
